<template>
  <b-card class="summary-card mb-0" no-body>

    <!-- PICTURE -->
    <b-img :src="accommodationPicture" class="summary-card__image" />

    <!-- NAME -->
    <div v-if="accommodationName" class="summary-card__name">
      <span class="font-weight-bold">{{ accommodationName }}</span>
    </div>

    <!-- STATS: CAPACITY | BEDROOMS | BATHROOMS -->
    <div class="d-flex border-top text-center">
      <div v-if="accommodationCapacity" class="summary-card__stat flex-fill"
        :class="{ 'border-right': accommodationBedrooms || accommodationBathrooms }">
        <div class="summary-card__stat-value font-weight-bold">{{ accommodationCapacity }}</div>
        <div class="summary-card__stat-label text-muted">{{ $t("Capacity") }}</div>
      </div>
      <div v-if="accommodationBedrooms" class="summary-card__stat flex-fill"
        :class="{ 'border-right': accommodationBathrooms }">
        <div class="summary-card__stat-value font-weight-bold">{{ accommodationBedrooms }}</div>
        <div class="summary-card__stat-label text-muted">{{ $t("Bedrooms") }}</div>
      </div>
      <div v-if="accommodationBathrooms" class="summary-card__stat flex-fill">
        <div class="summary-card__stat-value font-weight-bold">{{ accommodationBathrooms }}</div>
        <div class="summary-card__stat-label text-muted">{{ $t("Bathrooms") }}</div>
      </div>
    </div>

    <!-- LOCKBOX CODE -->
    <div v-if="accommodationLockboxCode" class="summary-card__info-row border-top d-flex align-items-center">
      <feather-icon icon="LockIcon" size="14" class="text-primary mr-75 flex-shrink-0" />
      <span class="small text-muted mr-1">{{ $t("Lockbox code") }}:</span>
      <span class="small font-weight-bold">{{ accommodationLockboxCode }}</span>
    </div>

    <!-- ADDRESS -->
    <div class="summary-card__info-row border-top d-flex align-items-start">
      <feather-icon icon="MapPinIcon" size="14" class="text-primary mr-75 flex-shrink-0 mt-25" />
      <span class="small">{{ fullAddress }}</span>
    </div>

    <!-- MAPS BUTTON -->
    <div v-if="latitude && longitude" class="summary-card__maps-btn">
      <b-button variant="flat-primary" block @click="openGoogleMapsLink(latitude, longitude)">
        <feather-icon icon="MapIcon" size="14" class="mr-50" />
        {{ $t("pages.accommodation.showOnGoogleMaps") }}
      </b-button>
    </div>

  </b-card>
</template>

<script>
import { BCard, BImg, BButton } from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import { ACCOMMODATION_PLACEHOLDER_SM } from "@consts";
import { getFullAddress } from "@methods";

export default {
  components: {
    BCard,
    BImg,
    BButton,
    FeatherIcon,
  },
  computed: {
    accommodation() {
      return this.$store.getters["accommodation/accommodation"];
    },
    accommodationPicture() {
      return (
        this.accommodation?.coverPicture?.thumbnailUrl ||
        ACCOMMODATION_PLACEHOLDER_SM
      );
    },
    accommodationName() {
      return this.accommodation?.name || null;
    },
    accommodationCapacity() {
      return this.accommodation?.capacity || null;
    },
    accommodationBedrooms() {
      return this.accommodation?.bedroomsNumber || null;
    },
    accommodationBathrooms() {
      return this.accommodation?.bathroomsNumber || null;
    },
    accommodationLockboxCode() {
      return this.accommodation?.details?.lockboxCode || null;
    },
    location() {
      return this.$store.getters["accommodation/location"];
    },
    fullAddress() {
      return (
        getFullAddress(this.location, this.$i18n.locale) ||
        this.$t("Not defined")
      );
    },
    latitude() {
      return this.location?.latitude || null;
    },
    longitude() {
      return this.location?.longitude || null;
    },
  },
  methods: {
    openGoogleMapsLink(latitude, longitude) {
      if (!latitude || !longitude) return;
      window.open(
        `https://www.google.com/maps/place/${latitude},${longitude}`,
        "_blank"
      );
    },
  },
};
</script>

<style lang="scss">
.summary-card {
  &__image {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 4px 4px 0 0;
  }

  &__name {
    padding: 1rem 1.25rem 0.875rem;
    font-size: 1.05rem;
  }

  &__stat {
    padding: 1rem 0.5rem;
  }

  &__stat-value {
    font-size: 1.1rem;
  }

  &__stat-label {
    font-size: 0.72rem;
    margin-top: 2px;
  }

  &__info-row {
    padding: 0.875rem 1.25rem;
  }

  &__maps-btn {
    padding: 0.5rem 1rem 1rem;
  }
}
</style>
